<script lang="ts" setup>
import { nextTick, ref, watch } from 'vue'
import type { Issue } from '@/interfaces/Issue'
import type { IssueCategory } from '@/interfaces/IssueCategory'
import { IssueService } from '@/services/IssueService'
import { ElNotification } from 'element-plus'
import {CheckOfflineService} from '@/services/CheckOfflineService'
import { useChecksStore } from '@/stores/checksStore'
import { isBlankOrEmpty } from '@/utils'
import Camera from '@/components/Camera.vue'

const emit = defineEmits(['closeDialog', 'onSend'])

const radio = ref<'high' | 'low'>('low')
const descriptionInput = ref('')
const checkId = ref<string>()
const taskId = ref<string>()
const checkOfflineService = CheckOfflineService();
const checkStore = useChecksStore();
const error = ref(false)
const reporterNameError = ref(false)
const props = defineProps({
  isOpen: { type: Boolean, required: true },
  title: { type: String },
  description: { type: String },
  hideSeverity: { type: Boolean }
})

const open = ref(false)

const categories = ref<IssueCategory[]>([])
const selectedCategoryId = ref<number>()
const reporterNameInput = ref('')

const cameraRef = ref<InstanceType<typeof Camera>>()
const showCamera = ref(false)
const photoBlob = ref<Blob | null>(null)
const photoPreviewUrl = ref<string | null>(null)
const uploadingPhoto = ref(false)

watch(
  () => props.isOpen,
  async (isOpen) => {
    open.value = isOpen

    if (isOpen) {
      if (categories.value.length === 0) {
        let issueService = new IssueService()
        categories.value = await issueService.getCategories()
      }
    } else {
      // Reset whenever the dialog is dismissed, however that happened
      // (Cancel, X, mask click, or after a successful send).
      resetForm()
    }
  }
)

const setCheckId = (newCheckId: string) => {
  checkId.value = newCheckId
}

const setTaskId = (newTaskId: string) => {
  taskId.value = newTaskId
}

defineExpose({ setCheckId, setTaskId })

const openCamera = async () => {
  showCamera.value = true
  await nextTick()
  await cameraRef.value?.start()
}

const onSnapshot = (blob: Blob | null) => {
  if (!blob) return

  photoBlob.value = blob
  photoPreviewUrl.value = URL.createObjectURL(blob)
  showCamera.value = false
}

const cancelCamera = () => {
  showCamera.value = false
}

const retakePhoto = () => {
  if (photoPreviewUrl.value) URL.revokeObjectURL(photoPreviewUrl.value)
  photoPreviewUrl.value = null
  photoBlob.value = null
  openCamera()
}

const removePhoto = () => {
  if (photoPreviewUrl.value) URL.revokeObjectURL(photoPreviewUrl.value)
  photoPreviewUrl.value = null
  photoBlob.value = null
}

const resetForm = () => {
  checkId.value = ''
  taskId.value = ''
  descriptionInput.value = ''
  selectedCategoryId.value = undefined
  reporterNameInput.value = ''
  error.value = false
  reporterNameError.value = false
  showCamera.value = false
  if (photoPreviewUrl.value) URL.revokeObjectURL(photoPreviewUrl.value)
  photoPreviewUrl.value = null
  photoBlob.value = null
}

const onSend = async () => {
  let issueService = new IssueService()

  if (isBlankOrEmpty(descriptionInput.value)) {
    error.value = true;
    return
  }

  if (isBlankOrEmpty(reporterNameInput.value)) {
    reporterNameError.value = true
    return
  }

  let issue: Issue = {
    severity: radio.value,
    description: descriptionInput.value,
    checkId: checkId.value == '' ? null : checkId.value,
    taskId: taskId.value == '' ? null : taskId.value,
    categoryId: selectedCategoryId.value ?? null,
    reporterName: reporterNameInput.value
  }

  if (photoBlob.value != null) {
    uploadingPhoto.value = true
    const uploaded = await issueService.uploadAttachment(photoBlob.value, `photo-${Date.now()}.jpg`)
    uploadingPhoto.value = false

    if (uploaded == null) {
      ElNotification({
        title: 'Failure',
        message: 'Failed to upload photo',
        type: 'error',
        customClass: 'notification'
      })
      return
    }

    issue.attachmentFileIds = [uploaded.fileId]
  }

  if (checkId.value != undefined && checkStore.currentCheckMetadata?.guid != null) {
    await checkOfflineService.addIssue(issue, checkStore.currentCheckMetadata?.guid!);

    ElNotification({
      title: 'Success',
      message: 'Corrective actions have been saved',
      type: 'success',
      customClass: 'notification'
    })
    emit('onSend')
  } else {
    let response = await issueService.saveIssue(issue)
    if (response.success) {
      ElNotification({
        title: 'Success',
        message: 'Issue has been saved',
        type: 'success',
        customClass: 'notification'
      })

      emit('onSend')
    } else {
      ElNotification({
        title: 'Failure',
        message: 'Unexpected error occurred',
        type: 'error',
        customClass: 'notification'
      })
    }
  }

  open.value = false
  resetForm()
}
</script>

<template>
  <el-dialog
    v-model="open"
    :title="title ? title : 'Report Issue'"
    align-center
    itemref="wrapper"
    width="500"
    @close="$emit('closeDialog')"
  >
    <div v-if="showCamera" class="camera-fullview">
      <Camera ref="cameraRef" @snapshot="onSnapshot" />
    </div>

    <el-space v-else class="container" direction="vertical" fill>
      <el-text class="description-label">{{ description ? description : 'Describe the issue' }}</el-text>
      <div v-if="error" style="color: red">Please enter a description</div>
      <el-input
        v-model="descriptionInput"
        resize="none"
        rows="4"
        style="width: 100%"
        type="textarea"
      />

      <div v-if="reporterNameError" style="color: red">Please enter your name</div>
      <el-input v-model="reporterNameInput" placeholder="Your name" style="width: 100%" />

      <div class="options-row">
        <el-radio-group v-if="!hideSeverity" v-model="radio" size="small" class="severity-toggle">
          <el-radio-button class="high-severity-button" value="High">High</el-radio-button>
          <el-radio-button class="low-severity-button" value="Low">Low</el-radio-button>
        </el-radio-group>

        <el-select
          v-if="categories.length > 0"
          v-model="selectedCategoryId"
          placeholder="Category (optional)"
          clearable
          size="small"
          class="category-select"
        >
          <el-option
            v-for="category in categories"
            :key="category.id"
            :label="category.name"
            :value="category.id"
          />
        </el-select>
      </div>

      <div style="width: 100%">
        <div v-if="photoPreviewUrl" class="photo-preview">
          <img :src="photoPreviewUrl" alt="Captured issue photo" class="photo-thumb" />
          <div class="photo-actions">
            <el-button class="no-hover cancel-button" @click="retakePhoto">Retake</el-button>
            <el-button class="no-hover cancel-button" @click="removePhoto">Remove</el-button>
          </div>
        </div>
        <el-button v-else class="no-hover photo-button" @click="openCamera">
          <i class="fas fa-camera"></i>&nbsp; Take a Photo
        </el-button>
      </div>
    </el-space>

    <template #footer>
      <div v-if="showCamera" class="dialog-footer">
        <el-button class="no-hover cancel-button" @click="cancelCamera">
          <i class="fas fa-arrow-left"></i>&nbsp; Back</el-button
        >
      </div>
      <div v-else class="dialog-footer">
        <el-button class="no-hover cancel-button" @click="open = false">Cancel</el-button>
        <el-button
          class="no-hover send-button"
          type="primary"
          :loading="uploadingPhoto"
          @click="onSend"
        >
          Send</el-button
        >
      </div>
    </template>
  </el-dialog>
</template>

<style scoped>
.container {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.description-label {
  font-weight: 600;
}

.options-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  width: 100%;
}

.category-select {
  flex: 1;
  min-width: 140px;
}

.high-severity-button {
  margin: 0;
  --el-radio-button-checked-bg-color: red;
}

.low-severity-button {
  margin: 0;
  --el-radio-button-checked-bg-color: #ffbf00;
}

/* Overrides the global .el-radio-button__inner sizing (main.css), which is
   tuned for the larger check-task Yes/No toggles, not this compact form. */
.severity-toggle :deep(.el-radio-button__inner) {
  font-size: 0.9rem !important;
  padding: 6px 14px !important;
}

.photo-button {
  width: 100%;
}

.dialog-footer {
  display: flex;
  gap: 12px;
  width: 100%;
}

.dialog-footer .el-button {
  flex: 1;
  margin-left: 0;
}

.send-button {
  background-color: #151740 !important;
  color: white !important;
  border-color: #151740;
  border-radius: 8px;
}

.cancel-button {
  background-color: #5d5d5d !important;
  color: white !important;
  border-radius: 8px;
}

.camera-fullview {
  width: 100%;
  height: 60vh;
  position: relative;
}

/* Camera.vue sizes itself for its other (full-page) usage in the task wizard
   via an inline max-height computed from window.innerHeight - 400, which
   doesn't match this dialog's height. Force it to fill our wrapper instead. */
.camera-fullview :deep(.camera-container) {
  max-height: 100% !important;
  height: 100%;
}

.camera-fullview :deep(.camera-video) {
  object-fit: cover;
}

.photo-preview {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
}

.photo-thumb {
  width: 72px;
  height: 72px;
  object-fit: cover;
  border-radius: 8px;
  flex-shrink: 0;
}

.photo-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
}

.photo-actions .el-button {
  width: 100%;
  margin-left: 0;
}
</style>
